import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Camera,
  Keyboard,
  RotateCcw,
  LogOut,
  User,
  Ticket,
  Calendar,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import { GAM_TICKET_LOGO } from '@/config/assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffScannerPage() {
  const navigate = useNavigate();
  const [staffUser, setStaffUser] = useState(null);
  const [mode, setMode] = useState('manual'); // 'manual' or 'camera'
  const [ticketInput, setTicketInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  useEffect(() => {
    // Check if staff is logged in
    const token = localStorage.getItem('staff_token');
    const user = localStorage.getItem('staff_user');
    
    if (!token || !user) {
      navigate('/staff/login');
      return;
    }
    
    setStaffUser(JSON.parse(user));
    
    return () => {
      stopScanner();
    };
  }, [navigate]);

  const startScanner = async () => {
    if (html5QrCodeRef.current) {
      stopScanner();
    }

    try {
      const html5QrCode = new Html5Qrcode("qr-scanner");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          // Vibrate on successful scan if supported
          if (navigator.vibrate) {
            navigator.vibrate(200);
          }
          handleValidate(decodedText);
          stopScanner();
        },
        () => {} // Ignore errors during scanning
      );
      setScanning(true);
    } catch (error) {
      console.error('Failed to start scanner:', error);
      toast.error('Could not access camera. Please use manual entry or check camera permissions.');
      setMode('manual');
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleValidate = async (ticketIdOrQr) => {
    if (!ticketIdOrQr.trim()) {
      toast.error('Please enter a ticket ID');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Extract ticket ID from QR code URL if needed
      let ticketId = ticketIdOrQr.trim().toUpperCase();
      if (ticketId.includes('/ticket/')) {
        ticketId = ticketId.split('/ticket/').pop().split('?')[0];
      }

      const token = localStorage.getItem('staff_token');
      const response = await axios.post(
        `${API_URL}/api/tickets/validate`,
        { ticket_id: ticketId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Map backend status to frontend format
      const backendStatus = response.data.status?.toUpperCase();
      const isValid = backendStatus === 'VALID';
      const isUsed = backendStatus === 'ALREADY_USED';

      const validationResult = {
        ...response.data,
        valid: isValid,
        status: isUsed ? 'used' : (isValid ? 'valid' : 'invalid'),
        ticketId,
        timestamp: new Date().toLocaleTimeString()
      };

      setResult(validationResult);
      setRecentScans(prev => [validationResult, ...prev.slice(0, 19)]);
      
      if (isValid) {
        toast.success('VALID - Entry Allowed!');
      } else if (isUsed) {
        toast.warning('Ticket already used');
      } else {
        toast.error(response.data.message || 'Invalid ticket');
      }
    } catch (error) {
      const errorResult = {
        valid: false,
        status: 'invalid',
        message: error.response?.data?.detail || error.response?.data?.message || 'Failed to validate ticket',
        ticketId: ticketIdOrQr,
        timestamp: new Date().toLocaleTimeString()
      };
      setResult(errorResult);
      setRecentScans(prev => [errorResult, ...prev.slice(0, 19)]);
      toast.error(errorResult.message);
    } finally {
      setLoading(false);
      setTicketInput('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleValidate(ticketInput);
  };

  const resetAndScanNext = () => {
    setResult(null);
    setTicketInput('');
    if (mode === 'camera') {
      startScanner();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_user');
    window.location.href = '/';
  };

  if (!staffUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#0055FF]">
              <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="font-bold text-sm">Ticket Scanner</p>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <User className="w-3 h-3" />
                {staffUser.name}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto p-4">
        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={mode === 'manual' ? 'default' : 'outline'}
            onClick={() => {
              stopScanner();
              setMode('manual');
              setResult(null);
            }}
            className={`flex-1 ${mode === 'manual' ? 'bg-[#0055FF]' : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'}`}
          >
            <Keyboard className="w-4 h-4 mr-2" />
            Enter Code
          </Button>
          <Button
            variant={mode === 'camera' ? 'default' : 'outline'}
            onClick={() => {
              setMode('camera');
              setResult(null);
              setTimeout(() => startScanner(), 100);
            }}
            className={`flex-1 ${mode === 'camera' ? 'bg-[#0055FF]' : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'}`}
          >
            <Camera className="w-4 h-4 mr-2" />
            Scan QR
          </Button>
        </div>

        {/* Validation Result */}
        {result && (
          <Card className={`mb-4 border-2 ${
            result.valid 
              ? 'border-green-500 bg-green-500/10' 
              : result.status === 'used'
                ? 'border-yellow-500 bg-yellow-500/10'
                : 'border-red-500 bg-red-500/10'
          }`}>
            <CardContent className="pt-6 text-center">
              <div className="mb-4">
                {result.valid ? (
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
                ) : result.status === 'used' ? (
                  <AlertTriangle className="w-20 h-20 text-yellow-500 mx-auto" />
                ) : (
                  <XCircle className="w-20 h-20 text-red-500 mx-auto" />
                )}
              </div>

              <h2 className={`text-2xl font-bold mb-2 ${
                result.valid ? 'text-green-500' : result.status === 'used' ? 'text-yellow-500' : 'text-red-500'
              }`}>
                {result.valid ? 'VALID' : result.status === 'used' ? 'ALREADY USED' : 'INVALID'}
              </h2>

              <p className="text-gray-400 text-sm mb-4">
                {result.valid ? 'Entry Allowed' : result.message}
              </p>

              {result.ticket && (
                <div className="bg-gray-800 rounded-lg p-3 text-left text-sm space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-[#0055FF]" />
                    <span className="font-mono font-bold">{result.ticket.ticket_id || result.ticketId}</span>
                  </div>
                  {result.ticket.event_name && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>{result.ticket.event_name}</span>
                    </div>
                  )}
                </div>
              )}

              <Button 
                onClick={resetAndScanNext}
                className="w-full bg-[#0055FF] hover:bg-[#0044CC]"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Scan Next Ticket
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Manual Entry */}
        {mode === 'manual' && !result && (
          <Card className="bg-gray-800 border-gray-700 mb-4">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Enter Ticket ID</label>
                  <Input
                    placeholder="TKT-123456"
                    value={ticketInput}
                    onChange={(e) => setTicketInput(e.target.value.toUpperCase())}
                    className="h-14 text-xl font-mono text-center bg-gray-900 border-gray-600 text-white placeholder:text-gray-500"
                    autoFocus
                    autoComplete="off"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-14 text-lg bg-[#0055FF] hover:bg-[#0044CC]"
                  disabled={loading || !ticketInput.trim()}
                >
                  {loading ? 'Validating...' : 'Validate Ticket'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Camera Scanner */}
        {mode === 'camera' && !result && (
          <Card className="bg-gray-800 border-gray-700 mb-4 overflow-hidden">
            <CardContent className="p-0">
              <div 
                id="qr-scanner" 
                ref={scannerContainerRef}
                className="w-full aspect-square bg-black"
              />
              {!scanning && (
                <div className="p-4">
                  <Button 
                    onClick={startScanner}
                    className="w-full bg-[#0055FF] hover:bg-[#0044CC]"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Camera
                  </Button>
                </div>
              )}
              {scanning && (
                <p className="text-center text-sm text-gray-400 p-4">
                  Point camera at QR code...
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recent Scans Toggle */}
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
          className="w-full bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
        >
          <History className="w-4 h-4 mr-2" />
          {showHistory ? 'Hide' : 'Show'} Scan History ({recentScans.length})
        </Button>

        {/* Scan History */}
        {showHistory && recentScans.length > 0 && (
          <div className="mt-4 space-y-2">
            {recentScans.map((scan) => (
              <div 
                key={`${scan.ticketId}-${scan.timestamp}`}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  scan.valid 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : scan.status === 'used'
                      ? 'bg-yellow-500/10 border border-yellow-500/30'
                      : 'bg-red-500/10 border border-red-500/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  {scan.valid ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : scan.status === 'used' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <p className="font-mono text-sm">{scan.ticketId}</p>
                    <p className="text-xs text-gray-500">{scan.timestamp}</p>
                  </div>
                </div>
                <Badge variant={scan.valid ? 'success' : scan.status === 'used' ? 'warning' : 'destructive'} className="text-xs">
                  {scan.valid ? 'Valid' : scan.status === 'used' ? 'Used' : 'Invalid'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
